/**
 * Dialogs Store - central registry for all modal dialogs and panels in Canvas storefront.
 *
 * **Key responsibilities:**
 * - Create and manage dialog instances via `createDialog` factory
 * - Provide type-safe dialog access across the application
 * - Configure dialog options (size, position, dismissible, etc.)
 * - Feature flag integration (e.g., simplified enrollment)
 *
 * **Architecture:**
 * - Each dialog has a unique ID matching its component name
 * - Dialog options sourced from `useAppConfig().components.dialog`
 * - `createDialog()` returns promise-based API: `.open()`, `.close()`
 *
 * **Dialog types:**
 * - **Modal**: Center-screen overlay (default)
 * - **Panel**: Side panel (e.g., mini-cart, quickshop)
 * - **Full-screen**: Mobile full-screen dialogs
 *
 * **Multi-brand notes:**
 * - All brands share same dialog registry
 * - Brand-specific behavior via component overrides
 * - Dialog options configurable per brand in app.config.ts
 *
 * **Usage pattern:**
 * ```ts
 * const { DialogSignIn, DialogMiniCart } = useDialogsStore()
 *
 * // Open dialog and wait for result
 * const result = await DialogSignIn.open()
 * if (result) {
 *   // User completed sign-in
 * }
 *
 * // Programmatic close
 * DialogMiniCart.close()
 * ```
 *
 * **Common dialogs:**
 * - Authentication: SignIn, SignUp, ForgotPassword, SignInConfirmation
 * - Commerce: MiniCart, Quickshop, PickupInStore, CartChangeShippingMethod
 * - Loyalty: LoyaltySignUp, MarketingEnrollment, Interests, ProfileEnrichment
 * - Payments: ThreeDsChallenge, Klarna, AddGiftOption
 * - User-generated content: AskQuestion, ReviewUgc
 * - Location: LocationConfirmation, ChangeStoreConfirmation, StoreChanged
 *
 * **Feature flags:**
 * - `allowSimplifiedEnrollment`: Switches SignIn to simplified enrollment flow
 *
 * **Technical notes:**
 * - Dialogs use `createDialog` helper from core utils
 * - Each dialog component must exist in `domains/commerce/components/dialog`
 * - Dialog options typed via `DialogOptions` interface
 *
 * @see domains/commerce/components/dialog for dialog components
 * @see domains/core/utils/dialog/createDialog for dialog factory
 * @see CONTRIBUTING.md#vuenuxt-composables---critical-rules for setup-only composable rules
 */
export const useDialogsStore = defineStore('dialogs', () => {
  const { allowPasswordLogin, allowSimplifiedEnrollment } = useFeatureFlags()
  const {
    cartChangeShippingMethod,
    changeStoreConfirmation,
    forgotPassword,
    languagePreference,
    locationConfirmation,
    miniCart,
    pickupInStore,
    pickupInStoreReserve,
    quickshop,
    signIn,
    signInConfirmation,
    signUp,
    simplifiedEnrollment,
    storeChanged,
    termsAcceptance
  } = useAppConfig().components.dialog

  // ────────────────────────────────────────────────────────────────────────────
  // Dialog Registry
  // ────────────────────────────────────────────────────────────────────────────

  return {
    // ──────────────────────────────────────────────────────────────────────────
    // Commerce Dialogs
    // ──────────────────────────────────────────────────────────────────────────

    /**
     * Add gift options to order (gift wrap, gift message).
     * **Context:** Checkout, cart
     */
    DialogAddGiftOption: createDialog('add-gift-option'),

    /**
     * Mini cart panel (slide-in from right).
     * **Context:** Add to cart, header cart icon
     * **Options:** Configurable size, dismissible behavior
     */
    DialogMiniCart: createDialog('mini-cart', miniCart?.dialogOptions),

    /**
     * Quick shop modal for product variants.
     * **Context:** PLP quick-view, related products
     */
    DialogQuickshop: createDialog('quickshop', quickshop?.dialogOptions),

    /**
     * Buy Online Pickup In Store (BOPIS) selection.
     * **Context:** PDP, cart (for available items)
     */
    DialogPickupInStore: createDialog('pickup-in-store', pickupInStore?.dialogOptions),

    /**
     * Reserve Online Buy In Store (ROBIS) selection.
     * **Context:** PDP, cart (for available items)
     */
    DialogPickupInStoreReserve: createDialog('pickup-in-store-reserve', pickupInStoreReserve?.dialogOptions),

    /**
     * Change shipping method in cart.
     * **Context:** Cart page (when multiple shipping options available)
     */
    DialogCartChangeShippingMethod: createDialog('cart-change-shipping-method', cartChangeShippingMethod?.dialogOptions),

    // ──────────────────────────────────────────────────────────────────────────
    // Authentication & User Dialogs
    // ──────────────────────────────────────────────────────────────────────────

    /**
     * Sign-in / Simplified enrollment dialog.
     * **Feature flag:** `allowSimplifiedEnrollment` switches to simplified flow
     * **Context:** Header, checkout (guest vs. registered)
     */
    DialogSignIn: createDialog(
      allowSimplifiedEnrollment ? 'simplified-enrollment' : 'sign-in',
      {
        ...signIn?.dialogOptions,
        ...(allowSimplifiedEnrollment ? { ...simplifiedEnrollment.dialogOptions, type: 'panel' } : {})
      }
    ),

    /**
     * Sign-in confirmation after successful authentication.
     * **Context:** Post-login flow
     */
    DialogSignInConfirmation: createDialog(
      allowPasswordLogin ? 'sign-in-confirmation' : 'simplified-enrollment',
      {
        ...signInConfirmation?.dialogOptions,
        ...(!allowPasswordLogin ? { ...simplifiedEnrollment.dialogOptions, type: 'panel' } : {})
      }
    ),

    /**
     * Sign-up / registration dialog.
     * **Context:** Header, checkout, loyalty enrollment
     */
    DialogSignUp: createDialog(
      allowPasswordLogin ? 'sign-up' : 'simplified-enrollment',
      {
        ...signUp?.dialogOptions,
        ...(!allowPasswordLogin ? { ...simplifiedEnrollment.dialogOptions, type: 'panel' } : {})
      }
    ),

    /**
     * Forgot password / reset password request.
     * **Context:** Sign-in dialog
     */
    DialogForgotPassword: createDialog('forgot-password', forgotPassword?.dialogOptions),

    /**
     * Employee terms & conditions acceptance.
     * **Context:** Employee login (required before discount use)
     */
    DialogEmployeeTerms: createDialog('employee-terms'),

    // ──────────────────────────────────────────────────────────────────────────
    // Loyalty & Personalization Dialogs
    // ──────────────────────────────────────────────────────────────────────────

    /**
     * Loyalty program enrollment.
     * **Context:** Account page, post-purchase
     */
    DialogLoyaltySignUp: createDialog('loyalty-sign-up'),

    /**
     * Marketing enrollment (SMS/email opt-in).
     * **Context:** Post-auth onboarding, sign-up flow
     */
    DialogMarketingEnrollment: createDialog('marketing-enrollment'),

    /**
     * User interests collection (preferences, categories).
     * **Context:** Post-auth onboarding, sign-up flow
     */
    DialogInterests: createDialog('interests'),

    /**
     * Profile enrichment (collect missing profile data).
     * **Context:** Post-auth onboarding (name, birthday, zip)
     */
    DialogProfileEnrichment: createDialog('profile-enrichment'),

    /**
     * Welcome dialog (final step in onboarding).
     * **Context:** Post-auth onboarding (after other dialogs)
     */
    DialogWelcome: createDialog('welcome'),

    // ──────────────────────────────────────────────────────────────────────────
    // Payment & Checkout Dialogs
    // ──────────────────────────────────────────────────────────────────────────

    /**
     * 3D Secure challenge iframe (payment verification).
     * **Context:** Checkout payment processing
     */
    DialogThreeDsChallenge: createDialog('three-ds-challenge'),

    /**
     * Klarna payment method description and calculated installments explanation.
     * **Context:** Checkout payment step
     */
    DialogKlarna: createDialog('klarna'),

    // ──────────────────────────────────────────────────────────────────────────
    // Location & Store Dialogs
    // ──────────────────────────────────────────────────────────────────────────

    /**
     * Location confirmation (country/region selection).
     * **Context:** First visit, country switcher
     */
    DialogLocationConfirmation: createDialog('location-confirmation', locationConfirmation?.dialogOptions),

    /**
     * Confirm store change (BOPIS context).
     * **Context:** Store locator, BOPIS flow
     */
    DialogChangeStoreConfirmation: createDialog('change-store-confirmation', changeStoreConfirmation?.dialogOptions),

    /**
     * Notification that preferred store changed.
     * **Context:** After store selection
     */
    DialogStoreChanged: createDialog('store-changed', storeChanged?.dialogOptions),

    // ──────────────────────────────────────────────────────────────────────────
    // User-Generated Content Dialogs
    // ──────────────────────────────────────────────────────────────────────────

    /**
     * Ask a question about a product.
     * **Context:** PDP (product details page)
     */
    DialogAskQuestion: createDialog('ask-question'),

    /**
     * Question submitted successfully confirmation.
     * **Context:** After ask-question submission
     */
    DialogAskQuestionSuccess: createDialog('ask-question-success'),

    /**
     * Review user-generated content (images, videos).
     * **Context:** Product reviews section
     */
    DialogReviewUgc: createDialog('review-ugc'),

    // ──────────────────────────────────────────────────────────────────────────
    // Experimental / Temporary Dialogs
    // ──────────────────────────────────────────────────────────────────────────

    /**
     * Language preference selection.
     * **TODO:** Cleanup once experiment GLOBAL15-108782 is complete.
     * **Context:** Multi-language sites
     */
    DialogLanguagePreference: createDialog('language-preference', languagePreference?.dialogOptions),

    /**
     * Terms and conditions acceptance before Apple Pay checkout.
     * **Context:** PDP and cart express checkout
     */
    DialogTermsAcceptance: createDialog('terms-acceptance', termsAcceptance?.dialogOptions),
  }
})
